import styled, { css } from 'styled-components';

export const Container = styled.div`
  width: 100%;
`;

export const TableContainer = styled.div`
  width: 100%;
  height: 19.2rem;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.greyVerySoft};
`;

export const Table = styled.table`
  width: 100%;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.S};
`;

export const Thead = styled.thead`
  background: ${({ theme }) => theme.colors.greyDark};
  color: ${({ theme }) => theme.colors.white};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
`;

export const Tbody = styled.tbody`
  background: ${({ theme }) => theme.colors.greyVerySoft};
  color: ${({ theme }) => theme.colors.black};
`;

interface trProps {
  checked?: boolean;
  indeterminate?: boolean;
}

export const Tr = styled.tr<trProps>`
  position: relative;
`;

/**
 * Relleno de la columna del SELECTOR (radio o casilla).
 *
 * 🔴 Llevaba el mismo relleno izquierdo que una columna de datos —32 px para un
 * control de 14—, así que ocupaba 48 px de los que dos tercios eran aire. En una
 * tabla estrecha ese aire se lo quita a la última columna: en «Plantillas de
 * recorrido», que va a media pantalla con dos tablas en fila, dejaba el botón de
 * eliminar fuera de la vista.
 *
 * Se selecciona por el CONTENIDO de la celda y no por «la primera columna»,
 * porque no todas las tablas empiezan por el selector — y así vale igual si
 * algún día se coloca en otro sitio. Va en el `Td`/`Th` compartidos, o sea que
 * afecta por igual a las 22 tablas que los usan.
 *
 * `width: 0` es el mismo truco que ya usa la columna de acciones: encoge la
 * celda a su contenido y devuelve el sobrante a las columnas de datos.
 *
 * Si un navegador no entendiera `:has()`, la regla se ignora y se queda
 * exactamente como estaba — el peor caso es el de hoy.
 */
const celdaDeSelector = css`
  &:has(input[type='radio']),
  &:has(input[type='checkbox']) {
    padding-left: ${({ theme }) => theme.spacing.s};
    padding-right: ${({ theme }) => theme.spacing.xxs};
    width: 0;
  }

  /**
   * La celda de ACCIONES, que es donde de verdad estaba el ancho.
   *
   * Medido en «Plantillas de recorrido» a 1512 px: 137 px de ancho para 73 de
   * botones — **64 px eran relleno**, más que la columna del selector entera.
   * La tabla pedía 645 px en una caja de 632 y se salía por 13, justo lo que
   * dejaba la papelera fuera.
   *
   * Los botones ya traen su propia separación entre ellos, así que el relleno
   * de columna de datos aquí no aporta nada: solo empuja.
   */
  &:last-of-type:has(button) {
    padding-left: ${({ theme }) => theme.spacing.s};
    padding-right: ${({ theme }) => theme.spacing.s};
  }
`;

export const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.xxs} 0
    ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.l};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;

  &:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md};
  }
  &:last-of-type {
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md};
    padding: ${({ theme }) => theme.spacing.xxs}
      ${({ theme }) => theme.spacing.l};
  }

  ${celdaDeSelector}
`;

export const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.xxs} 0
    ${({ theme }) => theme.spacing.xxs} ${({ theme }) => theme.spacing.l};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.greyLight};
  white-space: nowrap;
  text-overflow: ellipsis;
  vertical-align: middle;

  &:last-of-type {
    padding: ${({ theme }) => theme.spacing.xxs}
      ${({ theme }) => theme.spacing.l};
    width: 0;
  }

  ${celdaDeSelector}
`;

export const Tfoot = styled.tfoot`
  background: ${({ theme }) => theme.colors.greyLight};
  color: ${({ theme }) => theme.colors.black};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  position: sticky;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
`;

export const TfootTd = styled.td`
  padding: ${({ theme }) => theme.spacing.xs} 0
    ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.l};
  white-space: nowrap;
  vertical-align: middle;

  &:last-of-type {
    padding: ${({ theme }) => theme.spacing.xs}
      ${({ theme }) => theme.spacing.l};
  }
`;
